import { useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import useTrackingWebSocket from "@/features/tracking/hooks/useTrackingWebSocket";
import { invalidateRouteLists } from "@/features/routes/lib/invalidateRouteLists";
import { useAuthStore } from "@/store/auth";
import type { RouteStatusUpdate } from "@/types/tracking";

/**
 * Subscribes to live `route_status_update` frames on the tenant tracking
 * WebSocket and marks every view of the route stale (BUG-172 / BUG-173 /
 * BUG-738).
 *
 * Mounted ONCE, app-wide, in the dispatcher shell (`AppShell` →
 * `DispatcherShellBody`). It used to be mounted per page — RoutesPage
 * invalidated the lists, RouteDetailPage only its own route — so a frame that
 * arrived while the dispatcher was on the route page (or anywhere else) never
 * reached the routes table, and with the 5-minute global `staleTime` the table
 * kept «Активный» on return until a reload (BUG-738).
 *
 * Every frame goes through {@link invalidateRouteLists} with the frame's
 * `route_id`, so the lists, the dashboard, the maps, the chat route panel and
 * that route's detail queries all refetch (mounted) or refetch on next mount
 * (unmounted). Only `route_status_update` is handled — GPS `location_update`
 * pings change no route status and are deliberately not subscribed here.
 */
export function useRouteStatusLiveUpdates(): void {
  const queryClient = useQueryClient();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  const handleRouteStatusUpdate = useCallback(
    (update: RouteStatusUpdate) => {
      void invalidateRouteLists(queryClient, update.route_id);
    },
    [queryClient],
  );

  useTrackingWebSocket({
    onRouteStatusUpdate: handleRouteStatusUpdate,
    enabled: isAuthenticated,
  });
}
